/* eslint-disable max-len */
/* eslint-disable react/prop-types */
/* eslint-disable object-curly-newline */
import React, {
  useState, useEffect, useContext, useCallback, useMemo,
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Search } from 'lucide-react';
import moment from 'moment-timezone';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';
import useDebounce from '../../hooks/useDebounce';
import DataTable from '../../components/DataTable';
import Toggle from '../../components/Toggle';
import TimeSelector from '../../components/timeSelect';
import SelectTimeBox from './SelectTimeBox';
import SelectTimezoneBox from './SelectTimezoneBox';
import MemberCell from './MemberCell';
import { isTimeRangeWithin } from '../../utils/time';
import { STYLED_SELECT_CLASS, STYLED_SELECT_STYLE } from '../../utils/styledSelect';

const PAGE_SIZE_DEFAULT = 10;

function WorkingHours() {
  const { search } = useLocation();
  const history = useHistory();
  const query = new URLSearchParams(search);
  const page = query.get('page');
  const { activeWorkspace, user } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);
  const [rawData, setRawData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortParams, setSortParams] = useState({ sortKey: '', sortOrder: 'asc' });
  const { debounce } = useDebounce();
  const [currentPage, setCurrentPage] = useState(page ? parseInt(page, 10) : 1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [totalCounts, setTotalCounts] = useState(0);
  const [workingHoursPreference, setWorkingHoursPreference] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState(user.timezone);
  const [workspaceDefault, setWorkspaceDefault] = useState({
    workingDayStartTime: '', workingDayEndTime: '', workBreakStartTime: '', workBreakEndTime: '', timezone: '',
  });
  const [isDirty, setIsDirty] = useState(false);
  // Only one inline editor (schedule/timezone) open at a time across the table.
  const [openEditor, setOpenEditor] = useState(null);
  const toggleEditor = (key) => setOpenEditor((cur) => (cur === key ? null : key));

  const allTimezones = moment.tz.names();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(debounce((q) => { setSearchQuery(q); setCurrentPage(1); }, 500), []);
  const handleSearchChange = (e) => { setSearchInput(e.target.value); handleSearch(e.target.value); };

  const handlePageChange = (next) => { setCurrentPage(next); history.push(`?page=${next}`); };
  const handlePageSizeChange = (size) => { setCurrentPage(1); setPageSize(size); };

  const changeWorkingHours = (type, memberId) => {
    setWorkingHoursPreference({ ...workingHoursPreference, [memberId]: type });
    if (type === 'Default') {
      const tmpData = { ...rawData };
      const selectedMember = tmpData?.members?.find(({ id }) => id === memberId);
      if (selectedMember) {
        selectedMember.workingDayStartTime = null;
        selectedMember.workingDayEndTime = null;
        selectedMember.workBreakStartTime = null;
        selectedMember.workBreakEndTime = null;
      }
      setRawData(tmpData);
      sendRequest({ method: 'put', url: API.WORKSPACE.updateWorkingHours(activeWorkspace.id, memberId) });
    }
  };

  const handleUpdateWorkspaceDefault = (key, timeString) => {
    setWorkspaceDefault({ ...workspaceDefault, [key]: timeString });
    setIsDirty(true);
  };

  const submitWorkspaceWorkingSchedule = async () => {
    await sendRequest({
      method: 'put',
      url: API.WORKSPACE.updateWorkspaceWorkingHours(activeWorkspace.id),
      data: {
        workingDayStartTime: moment(workspaceDefault.workingDayStartTime, 'h:mm A').format('HH:mm:ss'),
        workingDayEndTime: moment(workspaceDefault.workingDayEndTime, 'h:mm A').format('HH:mm:ss'),
        timezone: workspaceDefault.timezone,
      },
    });
    setIsDirty(false);
    displayNotification({ message: 'Workspace working hours updated successfully', severity: 'success' });
  };

  const setMemberWorkingDayTime = async ({ timeStatus, startTime, endTime, memberId }) => {
    const tmpData = { ...rawData };
    const selectedMember = tmpData?.members?.find(({ id }) => id === memberId);
    const formatTime = (time) => moment.tz(time, 'h:mm A', 'Etc/UTC').utc().format('HH:mm:ss');
    const utcStartTime = formatTime(startTime);
    const utcEndTime = formatTime(endTime);

    const { workingDayStartTime, workingDayEndTime, workBreakStartTime, workBreakEndTime } = selectedMember;
    if (workingDayStartTime && workingDayEndTime && timeStatus === 'workBreak') {
      if (!isTimeRangeWithin(workingDayStartTime, workingDayEndTime, utcStartTime, utcEndTime)) {
        displayNotification({ message: 'Work breaks should be included in the working hours.', severity: 'error' });
        return;
      }
    } else if (workBreakStartTime && workBreakEndTime && timeStatus === 'workingHours') {
      if (!isTimeRangeWithin(utcStartTime, utcEndTime, workBreakStartTime, workBreakEndTime)) {
        selectedMember.workBreakStartTime = null;
        selectedMember.workBreakEndTime = null;
      }
    }

    const timeFields = {
      workingHours: { start: 'workingDayStartTime', end: 'workingDayEndTime' },
      workBreak: { start: 'workBreakStartTime', end: 'workBreakEndTime' },
    };
    const fields = timeFields[timeStatus];
    selectedMember[fields.start] = utcStartTime;
    selectedMember[fields.end] = utcEndTime;
    setRawData(tmpData);

    await sendRequest({
      method: 'put',
      url: API.WORKSPACE.updateWorkingHours(activeWorkspace.id, memberId),
      params: { [fields.start]: utcStartTime, [fields.end]: utcEndTime },
    });
    displayNotification({ message: 'Member working hours updated successfully', severity: 'success' });
  };

  const setMemberTimezone = ({ timezone, memberId }) => {
    setSelectedTimezone(timezone);
    sendRequest({
      method: 'put',
      url: API.WORKSPACE.updateWorkingHours(activeWorkspace.id, memberId),
      params: { workingScheduleTimezone: timezone },
    });
  };

  useEffect(() => {
    setIsLoading(true);
    sendRequest({
      method: 'GET',
      url: API.WORKSPACE.membersWorkingHours(activeWorkspace.id),
      params: { name: searchQuery, page: currentPage, pageSize, ...sortParams },
    }).then(({ data, success }) => {
      if (success) {
        const initialPreferences = data?.members.reduce((acc, { id, workingDayStartTime, workingDayEndTime }) => {
          acc[id] = workingDayStartTime || workingDayEndTime ? 'Custom' : 'Default';
          return acc;
        }, {});
        setWorkingHoursPreference(initialPreferences);
        setRawData(data);
        setTotalCounts(data?.pageInfo.totalCounts ?? 0);
        setWorkspaceDefault({
          ...data.organization,
          workingDayStartTime: moment(data.organization.workingDayStartTime, 'HH:mm:ss').format('h:mm A'),
          workingDayEndTime: moment(data.organization.workingDayEndTime, 'HH:mm:ss').format('h:mm A'),
          workBreakStartTime: moment(data.organization.workBreakStartTime, 'HH:mm:ss').format('h:mm A'),
          workBreakEndTime: moment(data.organization.workBreakEndTime, 'HH:mm:ss').format('h:mm A'),
        });
      }
    }).finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, sortParams, currentPage, pageSize]);

  const toggleSort = (key) => {
    setSortParams((p) => ({ sortKey: key, sortOrder: p.sortKey === key && p.sortOrder === 'asc' ? 'desc' : 'asc' }));
  };

  const mutedDefault = <p className="text-xs text-ink-500">Workspace Default</p>;

  const columns = useMemo(() => [
    {
      key: 'name',
      label: 'User',
      sortable: true,
      width: 240,
      flex: true,
      render: (m) => <MemberCell id={m.id} firstName={m.user?.firstName} lastName={m.user?.lastName} email={m.user?.email} profilePicture={m.user?.profilePicture} />,
    },
    {
      key: 'workingHours',
      label: 'Working Hours',
      width: 180,
      render: (m) => (
        <Toggle
          activeLabel={workingHoursPreference[m.id]}
          options={[
            { label: 'Default', onClick: () => changeWorkingHours('Default', m.id) },
            { label: 'Custom', onClick: () => changeWorkingHours('Custom', m.id) },
          ]}
          delay={0}
        />
      ),
    },
    {
      key: 'schedule',
      label: 'Schedule',
      width: 230,
      render: (m) => (workingHoursPreference[m.id] === 'Custom' ? (
        <SelectTimeBox id={m.id} timeStatus="workingHours" startTime={m.workingDayStartTime} endTime={m.workingDayEndTime} saveTime={setMemberWorkingDayTime} open={openEditor === `sched-${m.id}`} onToggle={() => toggleEditor(`sched-${m.id}`)} />
      ) : mutedDefault),
    },
    {
      key: 'timezone',
      label: 'Timezone',
      width: 220,
      render: (m) => (workingHoursPreference[m.id] === 'Custom' ? (
        <SelectTimezoneBox id={m.id} timeStatus="timezone" currentTimezone={m.workingScheduleTimezone} saveTimezone={setMemberTimezone} timezoneOptions={allTimezones} open={openEditor === `tz-${m.id}`} onToggle={() => toggleEditor(`tz-${m.id}`)} />
      ) : mutedDefault),
    },
    {
      key: 'action',
      label: 'Action',
      width: 170,
      render: (m) => (
        <button type="button" onClick={() => history.push(`/reports/time-worked?memberId=${m.id}`)} className="inline-flex items-center justify-center px-3 py-1.5 rounded-[8px] text-[11px] font-semibold uppercase tracking-wider text-ink-900 bg-white border border-rule hover:border-brand-500 hover:text-brand-700 transition whitespace-nowrap" style={{ borderStyle: 'solid', letterSpacing: '0.05em' }}>
          View Time Worked
        </button>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [workingHoursPreference, selectedTimezone, allTimezones, openEditor]);

  return (
    <div>
      <div className="p-4">
        <h4 className="text-base font-semibold text-ink-900 mb-0.5">Working Hours</h4>
        <p className="text-sm text-ink-500 mb-4">Specify the working hours for the team members in your organization.</p>

        {/* Workspace Default */}
        <span className="block text-sm font-medium text-ink-900 mb-2">Workspace Default</span>
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <span className="text-sm text-ink-500">Work from</span>
          <div style={{ width: 170 }}>
            <TimeSelector time={workspaceDefault.workingDayStartTime} setTime={(t) => handleUpdateWorkspaceDefault('workingDayStartTime', t)} />
          </div>
          <span className="text-sm text-ink-500">to</span>
          <div style={{ width: 170 }}>
            <TimeSelector time={workspaceDefault.workingDayEndTime} setTime={(t) => handleUpdateWorkspaceDefault('workingDayEndTime', t)} />
          </div>
          <span className="text-sm text-ink-500 ml-2">Timezone</span>
          <select
            value={workspaceDefault.timezone || ''}
            onChange={(e) => handleUpdateWorkspaceDefault('timezone', e.target.value)}
            className={STYLED_SELECT_CLASS}
            style={{ ...STYLED_SELECT_STYLE, minWidth: 220 }}
          >
            <option value="">Select timezone</option>
            {allTimezones.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </select>
          {isDirty && (
            <button type="button" onClick={submitWorkspaceWorkingSchedule} className="inline-flex items-center justify-center box-border h-10 px-4 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] transition">
              Save Changes
            </button>
          )}
        </div>

        <div style={{ height: 1, background: '#e0e1e6', margin: '0 0 16px' }} />

        {/* Individual Settings */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <span className="block text-sm font-medium text-ink-900">Individual Settings</span>
            <p className="text-sm text-ink-500">Override the workspace default for specific members</p>
          </div>
          <div className="relative flex-shrink-0" style={{ height: 40, width: 240 }}>
            <Search size={14} className="text-ink-500 pointer-events-none" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 1 }} />
            <input
              type="text"
              value={searchInput}
              onChange={handleSearchChange}
              placeholder="Search for user"
              className="w-full rounded-[8px] bg-white text-ink-900 placeholder:text-ink-500 border border-rule focus:outline-none focus:border-brand-500"
              style={{ height: 40, paddingLeft: 36, paddingRight: 12, fontSize: 14, lineHeight: '20px', borderStyle: 'solid', boxSizing: 'border-box' }}
            />
          </div>
        </div>
      </div>

      <DataTable
        bare
        columns={columns}
        rows={rawData?.members ?? []}
        getRowKey={(m) => m.id}
        sortKey={sortParams.sortKey}
        sortDir={sortParams.sortOrder}
        onSort={toggleSort}
        loading={isLoading}
        emptyText="No members found."
        page={currentPage}
        pageSize={pageSize}
        total={totalCounts}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        minWidth={1080}
      />
    </div>
  );
}

export default WorkingHours;
